/**
 * Turnstile challenge UI: show modal, dismiss, load script.
 * @package AdvancedTrackingManager
 */

import { getCookie } from '@lib/cookies';
import { createLogger } from '@lib/logger';
import { pushEvent } from '@lib/data-layer';
import { isFailTestSitekey } from './constants';
import { updateStatus } from './status';
import { verifyTokenServerSide } from './verify';

const w = globalThis as unknown as Window;

const getConfig = () => w.atmTurnstile;
const getDebug = () => createLogger('ATM Turnstile', getConfig()?.debugMode ?? false);

export const loadTurnstileScript = (): void => {
  if (document.querySelector('#cf-turnstile-script')) return;
  if (typeof (w as unknown as Record<string, unknown>).turnstile !== 'undefined') return;

  const script = document.createElement('script');
  script.id = 'cf-turnstile-script';
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  // Cloudflare requires no async/defer when using turnstile.ready().
  script.setAttribute('data-cookieconsent', 'ignore');
  script.addEventListener('error', () => {
    const config = getConfig();
    if (config) {
      updateStatus(
        { verified: false, pending: false, failed: true },
        config.siteKey,
        config.debugMode
      );
    }
  });
  document.head.append(script);
};

export const dismissChallenge = (overlay: HTMLElement): void => {
  const config = getConfig();
  if (!config) return;

  getDebug().debug('❌ Challenge dismissed by user - marking as failed');
  updateStatus(
    {
      verified: false,
      pending: false,
      failed: true,
      challengeShown: false,
      token: null,
    },
    config.siteKey,
    config.debugMode
  );
  getDebug().debug('🔍 Cookie after dismissal:', getCookie('atm_turnstile_status'));
  if (overlay?.parentNode) overlay.remove();
};

export const showChallenge = (siteKeyOverride?: string): void => {
  const config = getConfig();
  if (!config) return;

  const { siteKey, debugMode } = config;
  const status = w.atmTurnstileStatus;
  if (status?.challengeShown) {
    getDebug().debug('⚠️ Challenge already shown, skipping');
    return;
  }

  if (typeof w.turnstile === 'undefined') {
    getDebug().debug('❌ Turnstile API not available - not showing modal');
    updateStatus(
      { verified: false, pending: false, failed: true, challengeShown: false, token: null },
      siteKey,
      debugMode
    );
    return;
  }

  getDebug().debug('🔒 Showing visible Turnstile challenge (fallback)');
  updateStatus({ challengeShown: true }, siteKey, debugMode);

  const overlay = document.createElement('div');
  overlay.id = 'atm-turnstile-overlay';
  overlay.setAttribute('data-cookieconsent', 'ignore');
  overlay.style.cssText =
    'position: fixed; top: 0; left: 0; width: 100%; height: 100%; ' +
    'background: rgba(0, 0, 0, 0.8); z-index: 2147483632; ' +
    'display: flex; align-items: center; justify-content: center;';

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) dismissChallenge(overlay);
  });

  const challengeContainer = document.createElement('div');
  challengeContainer.id = 'atm-turnstile-challenge';
  challengeContainer.style.cssText =
    'position: relative; z-index: 2147483633; ' +
    'background: white; padding: 30px; border-radius: 12px; ' +
    'box-shadow: 0 8px 32px rgba(0,0,0,0.3); ' +
    'max-width: 450px; width: 90%; text-align: center; ' +
    'animation: atm-turnstile-fadein 0.3s ease-in;';

  challengeContainer.addEventListener('click', (e) => e.stopPropagation());

  if (!document.querySelector('#atm-turnstile-styles')) {
    const style = document.createElement('style');
    style.id = 'atm-turnstile-styles';
    style.textContent = `
        @keyframes atm-turnstile-fadein {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        #atm-turnstile-challenge h3 { margin: 0 0 20px 0; font-size: 20px; font-weight: 600; color: #333; }
        #atm-turnstile-challenge p { margin: 15px 0 0 0; font-size: 14px; color: #666; }
        #atm-turnstile-close {
          position: absolute; top: 10px; right: 10px; width: 32px; height: 32px;
          border: none; background: transparent; cursor: pointer; font-size: 24px; line-height: 1;
          color: #999; padding: 0; display: flex; align-items: center; justify-content: center;
          border-radius: 4px; transition: all 0.2s ease;
        }
        #atm-turnstile-close:hover { background: #f0f0f0; color: #333; }
        #atm-turnstile-close:active { background: #e0e0e0; }
      `;
    document.head.append(style);
  }

  const closeButton = document.createElement('button');
  closeButton.id = 'atm-turnstile-close';
  closeButton.innerHTML = '&times;';
  closeButton.setAttribute('aria-label', 'Close verification');
  closeButton.setAttribute('title', 'Close (verification will be marked as failed)');
  closeButton.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    dismissChallenge(overlay);
  });
  challengeContainer.append(closeButton);

  const title = document.createElement('h3');
  title.textContent = 'Please verify you are human';
  challengeContainer.append(title);

  const widgetDiv = document.createElement('div');
  widgetDiv.id = 'atm-turnstile-challenge-widget';
  widgetDiv.style.cssText = 'display: flex; justify-content: center; margin: 20px 0;';
  widgetDiv.setAttribute('data-cookieconsent', 'ignore');
  challengeContainer.append(widgetDiv);

  const description = document.createElement('p');
  description.textContent = 'Complete the verification to continue';
  challengeContainer.append(description);

  overlay.append(challengeContainer);
  document.body.append(overlay);

  const modalSiteKey = typeof siteKeyOverride === 'string' && siteKeyOverride ? siteKeyOverride : siteKey;
  let widgetLoadTimeout: ReturnType<typeof setTimeout> | null = null;

  const removeOverlayOnFailure = (): void => {
    if (widgetLoadTimeout !== null) {
      clearTimeout(widgetLoadTimeout);
      widgetLoadTimeout = null;
    }
    if (overlay?.parentNode) overlay.remove();
    w._atmTurnstileOverlay = null;
  };

  widgetLoadTimeout = setTimeout(() => {
    widgetLoadTimeout = null;
    if (!document.body.contains(overlay)) return;
    if (widgetDiv.querySelector('iframe')) return;
    getDebug().debug('❌ Turnstile widget did not appear within 12s - closing modal');
    updateStatus(
      { verified: false, pending: false, failed: true, challengeShown: false, token: null },
      siteKey,
      debugMode
    );
    removeOverlayOnFailure();
  }, 12000);

  w._atmTurnstileOverlay = overlay;

  const turnstileApi = w.turnstile as {
    render: (el: HTMLElement, opts: Record<string, unknown>) => string;
    ready?: (cb: () => void) => void;
  };
  const renderOpts: Record<string, unknown> = {
    sitekey: modalSiteKey,
    size: 'normal',
    theme: 'light',
    callback: async (token: string) => {
      if (!token || typeof token !== 'string' || token.length < 10) {
        updateStatus(
          { verified: false, serverVerified: false, pending: false, failed: true, challengeShown: false, token: null },
          siteKey,
          debugMode
        );
        return;
      }

      const currentOverlay = w._atmTurnstileOverlay ?? document.querySelector('#atm-turnstile-overlay');

      if (isFailTestSitekey(modalSiteKey)) {
        updateStatus(
          { verified: false, pending: false, failed: true, challengeShown: false, token: null },
          siteKey,
          debugMode
        );
        removeOverlayOnFailure();
        return;
      }

      updateStatus(
        { verified: true, serverVerified: false, pending: true, failed: false, challengeShown: false, token },
        siteKey,
        debugMode
      );

      const result = await verifyTokenServerSide(token, config.ajaxUrl);
      if (result.success) {
        updateStatus(
          { verified: true, serverVerified: true, pending: false, failed: false, challengeShown: false, token },
          siteKey,
          debugMode
        );
        if (currentOverlay && currentOverlay.parentNode) {
          removeOverlayOnFailure();
        }
      } else {
        updateStatus(
          { verified: false, serverVerified: false, pending: false, failed: true, challengeShown: false, token: null },
          siteKey,
          debugMode
        );
        if (currentOverlay && currentOverlay.parentNode) {
          removeOverlayOnFailure();
        }
      }

      pushEvent('turnstile_verified', {
        turnstile_token: token,
        turnstile_method: 'challenge',
      });
    },
    'error-callback': () => {
      updateStatus(
        { verified: false, pending: false, failed: true, challengeShown: false, token: null },
        siteKey,
        debugMode
      );
      removeOverlayOnFailure();
    },
    'expired-callback': () => {
      updateStatus(
        { verified: false, pending: false, failed: false, challengeShown: false, token: null },
        siteKey,
        debugMode
      );
      removeOverlayOnFailure();
    },
    'timeout-callback': () => {
      updateStatus(
        { verified: false, pending: false, failed: true, challengeShown: false, token: null },
        siteKey,
        debugMode
      );
      removeOverlayOnFailure();
    },
  };
  const doRender = (): void => {
    try {
      turnstileApi.render(widgetDiv, renderOpts);
    } catch (err) {
      getDebug().debug('❌ Turnstile render threw', err);
      updateStatus(
        { verified: false, pending: false, failed: true, challengeShown: false, token: null },
        siteKey,
        debugMode
      );
      removeOverlayOnFailure();
    }
  };
  requestAnimationFrame(() => {
    if (typeof turnstileApi.ready === 'function') {
      turnstileApi.ready(doRender);
    } else {
      doRender();
    }
  });
};
